// Falcon Platform screens: start-up, page routing and shared click/form handling.
import "./fieldfix.js";
import { ApiError, closeModal, esc, modalOpen, setIdentityGuard, setOrgGuard, toast } from "./lib.js";
import { OLD_ADDRESSES } from "../../shared/helpcentre.js";
import { MAIN_REPORTS, NOT_AVAILABLE, reportOfPath, reportShown } from "../../shared/reportsmenu.js";
import { menuLeaves, rowOf } from "../../shared/menu.js";
import { isAdminActor, landingPath } from "../../shared/access.js";
import { actions, changes, forms, matchRoute, navigate, onAction, onSessionChanged, refreshSession, setRenderHook, state } from "./state.js";
import { canOpen, closeActions, closeCalendar, closeSearch, errorPanel, renderShell } from "./shell.js";
import { applyTheme } from "./theme.js";
import { rememberedOrg } from "./profile.js";
import { beforeRender, noteScroll } from "./helpstash.js";
import { closePanel } from "./views/help.js";
import { isQcInspector } from "../../shared/permissions.js";
import { startFieldEnhancer } from "./fields.js";
import { ensureScreenLanguage } from "./screenlang.js";
import "./unlock.js";
import { authBrandHtml } from "./authbrand.js";
import "./views/auth.js";
import "./views/console.js";
import "./views/workspace.js";
import "./views/access.js";
import "./views/account.js";
import "./views/items.js";
import "./views/database.js";
import "./views/languages.js";
import "./views/datasheet.js";
import "./views/transfer.js";
import "./views/orders.js";
import "./views/critical.js";
import "./views/tasks.js";
import "./views/dashboard.js";
import "./views/codes.js";
import "./views/seals.js";
import "./views/timeline.js";
import "./views/sampling.js";
import "./views/dispatch.js";
import "./views/techpacks.js";
import "./views/techpack.js";
import "./views/artwork.js";
import "./views/cad.js";
import "./views/rd.js";
import "./views/qc.js";
import "./views/qcwizard.js";
import "./views/compliance.js";
import "./views/emaillog.js";
import "./views/help.js";
import "./views/shipments.js";
import "./views/packing.js";
import "./views/containerload.js";
import "./views/landed.js";
import "./views/finance.js";
import "./views/freight.js";
import "./views/finsettings.js";
import "./views/lc.js";
import "./views/customs.js";
import "./views/landeditems.js";
import "./views/ledgers.js";
import "./views/performance.js";
import "./views/setup.js";
import "./views/accexport.js";
import "./views/claims.js";
import "./views/supportdesk.js";
import "./views/legal.js";
import "./views/agreements.js";
import "./views/websiteprices.js";
import "./views/agree.js";
import "./views/orgdata.js";
import "./views/apisettings.js";
import "./views/crm.js";
import "./views/crmdeals.js";
import "./views/crmproducts.js";
import "./views/crmselling.js";
import "./views/crmsettings.js";
import "./views/crmreports.js";
import "./views/crmsalesreports.js";
import "./views/inventory.js";
import { bothPortals, crmOnly, crmOnlyPath, lastPortal } from "./views/crmaccess.js";

/** Settings, users and permissions: View or Full on the Settings row (changing them needs Full; the server checks). */
const isOrgAdmin = () => canOpen("settings");
/** The menu row a screen address belongs to (the longest menu address it starts with); none for Help, My Account… */
const ROW_PATHS: Array<[string, string]> = ([...menuLeaves().map((m) => [m.href, rowOf(m.id)]), ["/app/users", "settings"], ["/app/landed", "financelanded"], ["/app/inventory/docs", ""], ["/app/inventory/receive", "invreceive"]] as Array<[string, string]>)
  .filter(([h]) => h && h !== "/app").sort((x, y) => y[0].length - x[0].length);
function rowForPath(path: string): string | null {
  if (path === "/app") return "dashboard";
  return ROW_PATHS.find(([h]) => path === h || path.startsWith(`${h}/`))?.[1] ?? null;
}
const landing = () => (state.session.actor?.supportAccess ? "/app" : landingPath(state.session.actor?.access));

const PUBLIC = ["/login", "/forgot-password", "/reset-password", "/accept-invite"];

/** Decide where the person should be, given their sign-in state. */
function redirectFor(path: string): string | null {
  const s = state.session;
  // Invitation and reset links always open their own page, whoever is signed in on this browser.
  // …and so does the page that finishes signing in with Google, Microsoft or Apple.
  if (path === "/accept-invite" || path === "/reset-password" || path === "/sso-return") return null;
  if (!s.signedIn) {
    // A direct link (e.g. to a help page) opens after signing in, with the person's own access.
    if (path.startsWith("/app/")) { try { sessionStorage.setItem("falcon_return", path + location.search + location.hash); } catch { /* private window */ } }
    return PUBLIC.includes(path) ? null : "/login";
  }
  if (!s.mfaPassed) return path === "/login/verify" ? null : "/login/verify";
  if (s.needsTwoFactorSetup) return path === "/setup-two-factor" ? null : "/setup-two-factor";
  if (PUBLIC.includes(path)) return "/";
  if (path === "/login/verify" || path === "/setup-two-factor") return "/";
  if (path === "/workspaces") return null;
  // The legal documents come before anything else in an organisation (after two-factor and password screens).
  if (s.mode === "organisation" && s.legal) return path === "/agree" ? null : "/agree";
  if (path === "/agree") return s.mode === "organisation" ? "/app" : "/";

  if (s.mode === "platform") return path.startsWith("/console") ? null : "/console";
  if (s.mode === "organisation") {
    // Old addresses (bookmarks, emails) forward to the current screen straight away, before anything loads.
    if (OLD_ADDRESSES[path] && !isQcInspector(s.actor)) return OLD_ADDRESSES[path];
    // A QC inspector: only Ready for QC, their inspections, Help and their own account.
    if (isQcInspector(s.actor)) {
      const ok = /^\/app\/(qc\/(ready|mine|inspect\/[^/]+|report\/[^/]+)|help(\/.*)?|account)$/.test(path);
      return ok ? null : "/app/qc/ready";
    }
    // Falcon CRM only: Today is the start page, and no Falcon PLM screen opens (the server refuses them too).
    if (crmOnly()) return crmOnlyPath(path) && path !== "/app" ? null : "/app/crm";
    // A new organisation's main Admin: the set-up wizard opens on first sign-in (once per sign-in; "Finish later" leaves it).
    const org = s.organisation;
    if (path === "/app" && org?.mainAdmin && org.setupDone === false && !org.setupStarted) {
      let shown = false;
      try { shown = sessionStorage.getItem(`falcon_setup_shown:${org.id}`) === "1"; sessionStorage.setItem(`falcon_setup_shown:${org.id}`, "1"); } catch { /* private window */ }
      if (!shown) return "/app/setup";
    }
    let back = "";
    try { back = sessionStorage.getItem("falcon_return") || ""; sessionStorage.removeItem("falcon_return"); } catch { /* private window */ }
    if (back && path === "/app" && back.startsWith("/app/")) return back;
    // Falcon PLM and Falcon CRM: the portal the person used last opens (the switcher at the top of the menu changes it).
    if (path === "/app" && bothPortals() && lastPortal() === "crm") return "/app/crm";
    // The first menu item the person can open is where they land (only QC: Ready for QC).
    if (path === "/app" && !canOpen("dashboard")) return landing();
    return path.startsWith("/app") ? null : landing();
  }

  // Signed in, no workspace yet.
  const available = (s.workspaces || []).filter((w) => w.available);
  if (s.user?.isPlatformOwner && available.length === 0) return "__platform";
  if (!s.user?.isPlatformOwner && available.length === 1) return `__org:${available[0].orgId}`;
  // Several organisations: the one remembered on this device ("Remember my choice"), else the list to choose from.
  const remembered = !s.user?.isPlatformOwner ? rememberedOrg() : null;
  if (remembered && available.some((w) => w.orgId === remembered)) return `__org:${remembered}`;
  return "/workspaces";
}

let rendering = false;
let again = false;
/** The page (path + query) on screen: a change of only the #hash doesn't re-render it (unsaved work is kept). */
let renderedAt = "";
async function render(): Promise<void> {
  if (rendering) { again = true; return; }
  rendering = true;
  try {
    do {
      again = false;
      const path = location.pathname.replace(/\/+$/, "") || "/";
      // The person's screen language (English on the sign-in screens), ready before the screen is drawn.
      await ensureScreenLanguage();
      const target = redirectFor(path);
      if (target === "__platform" || target?.startsWith("__org:")) {
        const { post } = await import("./lib.js");
        await post("/api/session/workspace", target === "__platform" ? { mode: "platform" } : { orgId: target.slice(6) });
        await refreshSession();
        history.replaceState(null, "", target === "__platform" ? "/console" : "/app");
        again = true;
        continue;
      }
      if (target && target !== path) {
        history.replaceState(null, "", target);
        again = true;
        continue;
      }
      const match = matchRoute(path);
      if (!match) {
        history.replaceState(null, "", "/");
        again = true;
        continue;
      }
      // Settings (and users & permissions) are for the organisation's Admins only; the server refuses the rest too.
      if (/^\/app\/(settings|users)(\/|$)/.test(path) && !isOrgAdmin()) {
        renderShell({ active: "settings", title: "Settings", subtitle: "", content: errorPanel("You do not have access to this page. Settings are for users with access to Settings; ask an Admin if something needs changing.") });
        continue;
      }
      // The main Reports menu is hidden: an old link to one of its reports says so, with a way back to the dashboard.
      const hiddenReport = reportOfPath(path);
      if (hiddenReport && !reportShown(state.session.reportsMenu, hiddenReport)) {
        closePanel();
        renderedAt = location.pathname + location.search;
        renderShell({ active: "", title: MAIN_REPORTS.find((m) => m.id === hiddenReport)!.label, subtitle: "", content: `<div class="panel report-unavailable" role="status"><p><strong>${NOT_AVAILABLE}</strong></p><p><a class="primary btn" href="/app" data-link>Go to the dashboard</a></p></div>` });
        continue;
      }
      // A page the person can't open (direct link or old bookmark): say so, with a link to where they land.
      const row = rowForPath(path);
      // Inventory: Admins (Full on Settings) always have Full there.
      if (row && !canOpen(row) && !(row.startsWith("inv") && isAdminActor(state.session.actor))) {
        closePanel();
        renderedAt = location.pathname + location.search;
        renderShell({ active: "", title: "No access", subtitle: "", content: `<div class="panel report-unavailable no-access" role="status"><p><strong>You do not have access to this page.</strong></p><p class="muted">Ask an Admin if you need it.</p><p><a class="primary btn" href="${landing()}" data-link>Go to my start page</a></p></div>` });
        continue;
      }
      // Help keeps the page it was opened from and gives it back, unchanged, when Help is closed.
      closePanel();
      const restored = beforeRender(renderedAt, location.pathname + location.search);
      renderedAt = location.pathname + location.search;
      if (restored) continue;
      await match.render({ params: match.params, query: new URLSearchParams(location.search), path });
    } while (again);
  } catch (err) {
    handleError(err);
    // A screen that couldn't load says why (for example "Falcon is busy") instead of showing "Loading…" for ever.
    const content = document.getElementById("content");
    if (content && !content.querySelector(".load-failed") && !(err instanceof ApiError && ["NOT_SIGNED_IN", "MFA_REQUIRED", "MFA_SETUP_REQUIRED", "NO_WORKSPACE", "AGREEMENT_REQUIRED", "IDENTITY_CHANGED"].includes(err.code))) {
      const box = document.createElement("div");
      box.className = "panel load-failed";
      box.setAttribute("role", "alert");
      box.innerHTML = `<p>${esc(err instanceof Error ? err.message : "Something went wrong.")} <a href="${esc(location.pathname + location.search)}" data-link>Try again</a></p>`;
      const loading = [...content.querySelectorAll<HTMLElement>(".empty")].find((e) => e.textContent === "Loading…");
      if (loading) (loading.closest(".panel") || loading).replaceWith(box); else content.append(box);
    }
  } finally {
    rendering = false;
  }
}
setRenderHook(render);
applyTheme();
startFieldEnhancer();

async function handleError(err: unknown): Promise<void> {
  if (err instanceof ApiError && ["NOT_SIGNED_IN", "MFA_REQUIRED", "MFA_SETUP_REQUIRED", "NO_WORKSPACE", "AGREEMENT_REQUIRED"].includes(err.code)) {
    await refreshSession(true).catch(() => {});
    await navigate(err.code === "NOT_SIGNED_IN" ? "/login?expired=1" : "/", true);
    return;
  }
  console.error(err);
  toast(err instanceof Error ? err.message : "Something went wrong.", "error");
}
window.addEventListener("unhandledrejection", (e) => {
  e.preventDefault();
  handleError(e.reason);
});

// ---- Shared event handling ------------------------------------------------------------
onAction("close-modal", () => closeModal());

document.addEventListener("click", (e) => {
  const target = e.target as HTMLElement;
  if (!target.closest("#header-date-time-wrap")) closeCalendar();
  if (!target.closest(".falcon-global-search") || target.closest(".falcon-search-hit")) closeSearch();
  if (!target.closest(".falcon-bell-wrap") || target.closest(".falcon-action-row")) closeActions();
  const actionEl = target.closest<HTMLElement>("[data-action]");
  if (actionEl && actions[actionEl.dataset.action!]) {
    if (actionEl.tagName === "A") e.preventDefault();
    Promise.resolve(actions[actionEl.dataset.action!](actionEl, e)).catch(handleError);
    return;
  }
  const link = target.closest<HTMLAnchorElement>("a[data-link]");
  if (link && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.button === 0) {
    e.preventDefault();
    navigate(link.getAttribute("href") || "/");
  }
  if (target.id === "modal") closeModal();
});

document.addEventListener("submit", (e) => {
  const form = e.target as HTMLFormElement;
  const handler = forms[form.dataset.form || ""];
  if (!handler) return;
  e.preventDefault();
  Promise.resolve(handler(form, e as SubmitEvent)).catch(handleError);
});

document.addEventListener("change", (e) => {
  const el = (e.target as HTMLElement).closest<HTMLElement>("[data-change]");
  if (el && changes[el.dataset.change!]) Promise.resolve(changes[el.dataset.change!](el, e)).catch(handleError);
});
// A red field clears as soon as it is fixed (web/src/fieldfix.ts).
document.addEventListener("input", (e) => {
  const el = e.target as HTMLElement;
  if (el.dataset.change === "org-price") changes["org-price"]?.(el, e);
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    closeCalendar();
    closeActions();
    document.body.classList.remove("falcon-menu-open");
    if (modalOpen()) closeModal();
  }
});

// ---- One browser, one person ------------------------------------------------------------
// If this browser's sign-in changes (another tab signed in as someone else, or signed out),
// this page must not carry on showing the old person's screens. Reload from scratch.
let reloading = false;
function identityChanged(): void {
  if (reloading) return;
  reloading = true;
  document.getElementById("root")!.innerHTML = `<div class="falcon-auth-page"><div class="falcon-auth-card">${authBrandHtml("")}<div class="falcon-auth-body"><h1>Signed-in account changed</h1><p class="falcon-auth-sub">Someone signed in or out in another tab of this browser. This page is reloading…</p></div></div></div>`;
  location.replace("/");
}
const shownUser = () => (state.session.signedIn && state.session.mfaPassed ? state.session.user?.id ?? null : null);
setIdentityGuard((who) => {
  const mine = shownUser();
  if (mine && who !== mine) {
    identityChanged();
    return true;
  }
  return false;
});
// One organisation at a time: if another tab switched organisation, this page never shows the old one's data next to
// the new one's; it opens the new organisation's Home from scratch.
setOrgGuard((org) => {
  const s = state.session;
  if (!s.signedIn || !s.mfaPassed || s.mode !== "organisation" || !s.organisation) return false;
  if (org === s.organisation.id) return false;
  if (!reloading) { reloading = true; location.replace("/app"); }
  return true;
});
onSessionChanged((before, after) => {
  if (before && before !== after) identityChanged();
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && shownUser()) refreshSession().catch(() => {});
});

window.addEventListener("popstate", () => {
  if (location.pathname + location.search === renderedAt && location.hash) { window.dispatchEvent(new Event("falcon:hash")); return; }
  noteScroll(window.scrollY);
  if (modalOpen()) closeModal();
  render();
});

// ---- Start ----------------------------------------------------------------------------
(async () => {
  try {
    await refreshSession();
  } catch {
    document.getElementById("root")!.innerHTML = `<div class="falcon-auth-page"><div class="falcon-auth-card">${authBrandHtml("")}<div class="falcon-auth-body"><h1>Falcon can't be reached</h1><p class="falcon-auth-sub">The server didn't answer. Check your internet connection and try again in a minute.</p><button class="primary" type="button" data-action="reload">Try Again</button></div></div></div>`;
    return;
  }
  await render();
})();
onAction("reload", () => location.reload());

void esc;
